import {
  AppointmentBookedDetails,
  BookingMode,
  CALENDAR_CONFIGURATION_ADMIN_STATUS,
  CALENDAR_PROVIDER_ADMIN_STATUS,
  CalendarProvider,
  EventModality,
  ProviderCalendarConfiguration,
  ProviderCalendarEvent,
  ProviderGoogleEvent,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'

import { AxiosInstance, baseApi, RTKQueryTag } from '../../data/baseApi'

type OAuthURLRequest = {
  providerEmail: string
  providerId: string
  timeZone: string
}

type OAuthURLResponse = {
  authorization_uri: string
}

type ProviderIdRequest = {
  providerId: string
}

type GetCalendarTokenResponse = {
  access_token: string
}

type GetCalendarEventsRequest = {
  providerId: string
  startDate: string
  endDate: string
}

type PostCalendarEventsTemplateRequest = {
  provider_calendar_configuration_id: string
  events: {
    title: string
    start: string
    end: string
    timezone: string
    event_type: string
    recurrence?: string[]
    modality_availability?: EventModality | null
    booking_type?: string[] | null
  }[]
}

type PutCalendarEventsTemplateCancelRequest = {
  provider_calendar_configuration_id: string
  event_ids: string[]
}

type PutAvailableCalendarsRequest = {
  provider_id: string
  configurations: ProviderCalendarConfiguration[]
}

type PatchCalendarEventRequest = {
  external_id: string
  body: {
    provider_calendar_configuration_id: string
    event: {
      start?: string
      end?: string
      status?: string
      title?: string
      modality_availability?: EventModality | null
      booking_type?: string[] | null
    }
  }
}

type PatchCalendarProviderRequest = {
  providerId: string
  body: {
    admin_status?: CALENDAR_PROVIDER_ADMIN_STATUS
    booking_mode?: BookingMode
    daily_start?: string
    daily_end?: string
  }
}

type PostCalendarEventsRequest = {
  provider_calendar_configuration_id: string
  events: {
    title: string
    start: string
    end: string
    timezone: string
    event_type: string
    modality_availability?: EventModality | null
    booking_type?: string[] | null
  }[]
}

type GetGoogleEventRequest = {
  external_id: string
  provider_calendar_configuration_id: string
}

type GetCalendarProviderRequest = {
  providerId: string
}

type GetAppointmentBookedDetailsRequest = {
  appointmentId: number
}

type PatchCalendarConfigurationRequest = {
  configurationId: string
  body: {
    admin_status: CALENDAR_CONFIGURATION_ADMIN_STATUS
  }
}

type PutVideoPlatformRequest = {
  provider_id: string
  platform: VIDEO_PLATFORM
}

type PutVideoPlatformResponse = {
  status: string
  provider_id: string
  platform: VIDEO_PLATFORM
  skipped: boolean
  reason: 'duplicate_request' | 'non_hybrid_booking_mode' | null
}
const calendarApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOAuthURL: builder.query<OAuthURLResponse, OAuthURLRequest>({
      query: ({ providerEmail, providerId, timeZone }) => ({
        method: 'get',
        url: '/oauth2/google',
        params: {
          provider_email: providerEmail,
          provider_id: providerId,
          timezone: timeZone,
        },
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
    }),
    getCalendarToken: builder.query<GetCalendarTokenResponse, ProviderIdRequest>({
      query: ({ providerId }) => ({
        method: 'get',
        url: `/api/v1/auth/${providerId}`,
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
    }),
    getCalendarEvents: builder.query<ProviderCalendarEvent[], GetCalendarEventsRequest>({
      query: ({ providerId, startDate, endDate }) => ({
        method: 'get',
        url: `/api/v1/events`,
        params: {
          start_date: startDate,
          end_date: endDate,
          provider_id: providerId,
        },
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
    }),
    getCalendarAvailabilitySlots: builder.query<ProviderCalendarEvent[], ProviderIdRequest>({
      query: ({ providerId }) => ({
        method: 'get',
        url: `/api/v1/events`,
        params: {
          event_type: 'bookable_recurring',
          provider_id: providerId,
        },
        axiosInstanceType: AxiosInstance.CALENDAR,
        ignore404: true,
      }),
      providesTags: [RTKQueryTag.EVENTS_TEMPLATE],
    }),
    postCalendarEventsTemplate: builder.mutation<void, PostCalendarEventsTemplateRequest>({
      query: (body) => ({
        method: 'post',
        url: `/api/v1/events/template`,
        data: body,
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
      invalidatesTags: [RTKQueryTag.EVENTS_TEMPLATE],
    }),
    putCalendarEventsTemplateCancel: builder.mutation<void, PutCalendarEventsTemplateCancelRequest>({
      query: (body) => ({
        method: 'put',
        url: `/api/v1/events/template/cancel`,
        data: body,
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
      invalidatesTags: [RTKQueryTag.EVENTS_TEMPLATE],
    }),
    getCalendars: builder.query<ProviderCalendarConfiguration[], ProviderIdRequest>({
      query: ({ providerId }) => ({
        method: 'get',
        url: `/api/v1/calendar/configuration`,
        params: {
          provider_id: providerId,
        },
        axiosInstanceType: AxiosInstance.CALENDAR,
        ignore404: true,
      }),
      providesTags: [RTKQueryTag.CALENDAR_CONFIGURATIONS],
    }),
    getAvailableCalendars: builder.query<ProviderCalendarConfiguration[], ProviderIdRequest>({
      query: ({ providerId }) => ({
        method: 'get',
        url: `/api/v1/calendar/configuration/available`,
        params: {
          provider_id: providerId,
        },
        axiosInstanceType: AxiosInstance.CALENDAR,
        ignore404: true,
      }),
      providesTags: [RTKQueryTag.AVAILABLE_CALENDARS],
    }),
    putAvailableCalendars: builder.mutation<void, PutAvailableCalendarsRequest>({
      query: ({ provider_id, configurations }) => ({
        method: 'put',
        url: `/api/v1/calendar/configuration/provider/${provider_id}`,
        data: { configurations },
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
      invalidatesTags: [RTKQueryTag.AVAILABLE_CALENDARS, RTKQueryTag.CALENDAR_CONFIGURATIONS],
    }),
    patchCalendarEvent: builder.mutation<void, PatchCalendarEventRequest>({
      query: ({ external_id, body }) => ({
        method: 'patch',
        url: `/api/v1/events/${external_id}`,
        data: body,
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
    }),
    postCalendarEvents: builder.mutation<ProviderCalendarEvent[], PostCalendarEventsRequest>({
      query: (request) => ({
        method: 'post',
        url: `/api/v1/events`,
        data: request,
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
    }),
    updateCalendarEventsVideoPlatform: builder.mutation<PutVideoPlatformResponse, PutVideoPlatformRequest>({
      query: (body) => ({
        method: 'put',
        url: `/api/v1/events/video-platform`,
        data: body,
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
      invalidatesTags: [RTKQueryTag.EVENTS_TEMPLATE],
    }),
    getGoogleEvent: builder.query<ProviderGoogleEvent, GetGoogleEventRequest>({
      query: (request) => ({
        method: 'get',
        url: `/api/v1/events/${request.external_id}`,
        params: {
          provider_calendar_configuration_id: request.provider_calendar_configuration_id,
        },
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
    }),
    getCalendarProvider: builder.query<CalendarProvider, GetCalendarProviderRequest>({
      query: ({ providerId }) => ({
        method: 'get',
        url: `/api/v1/provider/${providerId}`,
        axiosInstanceType: AxiosInstance.CALENDAR,
        ignore404: true,
      }),
      providesTags: [RTKQueryTag.CALENDAR_PROVIDER],
    }),
    patchCalendarProvider: builder.mutation<void, PatchCalendarProviderRequest>({
      query: ({ providerId, body }) => ({
        method: 'patch',
        url: `/api/v1/provider/${providerId}`,
        data: body,
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
      invalidatesTags: [RTKQueryTag.CALENDAR_PROVIDER],
    }),
    getAppointmentBookedDetails: builder.query<AppointmentBookedDetails, GetAppointmentBookedDetailsRequest>({
      query: ({ appointmentId }) => ({
        method: 'get',
        url: `/v1/providers/appointment-booked-details/${appointmentId}`,
      }),
    }),
    patchCalendarConfiguration: builder.mutation<void, PatchCalendarConfigurationRequest>({
      query: ({ configurationId, body }) => ({
        method: 'patch',
        url: `/api/v1/calendar/configuration/${configurationId}`,
        data: body,
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
      invalidatesTags: [RTKQueryTag.CALENDAR_CONFIGURATIONS],
    }),
  }),
})

export const {
  useLazyGetOAuthURLQuery,
  useGetCalendarTokenQuery,
  useLazyGetCalendarEventsQuery,
  useGetCalendarAvailabilitySlotsQuery,
  usePostCalendarEventsTemplateMutation,
  usePutCalendarEventsTemplateCancelMutation,
  useGetCalendarsQuery,
  useGetAvailableCalendarsQuery,
  useLazyGetAvailableCalendarsQuery,
  usePutAvailableCalendarsMutation,
  usePatchCalendarEventMutation,
  useUpdateCalendarEventsVideoPlatformMutation,
  usePostCalendarEventsMutation,
  useLazyGetGoogleEventQuery,
  useGetCalendarProviderQuery,
  useLazyGetCalendarProviderQuery,
  usePatchCalendarProviderMutation,
  useLazyGetAppointmentBookedDetailsQuery,
  usePatchCalendarConfigurationMutation,
} = calendarApi
